import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { aiAPI } from "../lib/api";
import { useSettings } from "./SettingsContext";

interface AIConfigContextValue {
  /** null = loading, true = configured, false = not configured */
  aiConfigured: boolean | null;
  /** Whether any AI config has been saved (even if connection verification failed) */
  aiHasConfig: boolean;
  /** Whether the user has enabled AI features (master toggle) */
  aiEnabled: boolean;
  /** AI is both enabled by the user AND has a configured provider */
  aiReady: boolean;
  /** Config exists but has never been verified against the provider */
  aiNeedsVerification: boolean;
  /** Whether the user's tier allows BYOK (bring-your-own-key) configuration */
  byokAllowed: boolean;
  /** Force a re-check of the AI configuration status */
  refreshAIStatus: () => Promise<void>;
}

const AIConfigContext = createContext<AIConfigContextValue>({
  aiConfigured: null,
  aiHasConfig: false,
  aiEnabled: true,
  aiReady: false,
  aiNeedsVerification: false,
  byokAllowed: false,
  refreshAIStatus: async () => {},
});

export function AIConfigProvider({ children }: { children: ReactNode }) {
  const [aiConfigured, setAiConfigured] = useState<boolean | null>(null);
  const [aiHasConfig, setAiHasConfig] = useState(false);
  const [aiNeedsVerification, setAiNeedsVerification] = useState(false);
  const [byokAllowed, setByokAllowed] = useState(false);
  const { settings } = useSettings();
  const location = useLocation();

  const refreshAIStatus = useCallback(async () => {
    try {
      const status = await aiAPI.getStatus();
      setAiConfigured(status.configured);
      setAiHasConfig(status.hasConfig ?? status.configured);
      setAiNeedsVerification(status.needsVerification ?? false);
      setByokAllowed(status.byokAllowed ?? false);
    } catch {
      setAiConfigured(false);
    }
  }, []);

  // Re-check AI status on every route change so the CTA
  // always reflects the true server-side state.
  useEffect(() => {
    refreshAIStatus();
  }, [location.pathname, refreshAIStatus]);

  const aiEnabled = settings.aiEnabled;
  const aiReady = aiEnabled && aiConfigured === true;

  return (
    <AIConfigContext.Provider
      value={{
        aiConfigured,
        aiHasConfig,
        aiEnabled,
        aiReady,
        aiNeedsVerification,
        byokAllowed,
        refreshAIStatus,
      }}
    >
      {children}
    </AIConfigContext.Provider>
  );
}

export function useAIConfig() {
  return useContext(AIConfigContext);
}
